import {computed, Injectable, signal} from '@angular/core';
import {MAPS_TYPES_ENUM} from '../../editor-interface/shared/models/map.model';
import {DiagramMetaInterface} from '../../diagram-data/diagram-meta.interface';
import {DiagramBaseConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-base-config';
import {ArchitectureBaseConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-architecture-base-config';
import {AgentInstructionConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-agent-instruction-config';
import {AgentInteractionConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-agent-interaction-config';
import {InteractionFlowConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-interaction-flow-config';
import {CapabilityConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-capability-config';
import {DataModelConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-data-model-config';
import {
  ActivityType,
  RichTextSettings,
  StickerNoteSettings
} from '../../diagram-render/styles/diagram-type-styles.model';
import {UPNConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-upn-config';
import {CustomerJourneyConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-customer-journey-config';
import {SystemArchitectureConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-system-architecture-config';
import {WhiteboardConfig} from '../../diagram-render/styles/diagram-subtypes-configs/diagram-whiteboard-config';
import {DiagramSubtypeUrlMap} from '@q9elements/ui-kit/common';

export const ARCHITECTURE_TYPES = [
  MAPS_TYPES_ENUM.AGENT_INTERACTION,
  MAPS_TYPES_ENUM.INTERACTION_FLOW,
  MAPS_TYPES_ENUM.CAPABILITY,
  MAPS_TYPES_ENUM.DATA_MODEL,
  MAPS_TYPES_ENUM.SYSTEM_ARCHITECTURE
];
export const UPN_TYPES = [
  MAPS_TYPES_ENUM.UPN,
  MAPS_TYPES_ENUM.AGENT_INSTRUCTION,
  MAPS_TYPES_ENUM.CUSTOMER_JOURNEY
];

export const AGENT_TYPES = [MAPS_TYPES_ENUM.AGENT_INSTRUCTION, MAPS_TYPES_ENUM.AGENT_INTERACTION];

@Injectable({
  providedIn: 'root'
})
export class DiagramTypeConfigService {
  private _currentType = signal<MAPS_TYPES_ENUM>(MAPS_TYPES_ENUM.UPN);
  public currentType = this._currentType.asReadonly();
  public isArchitecture = computed(() => {
    return ARCHITECTURE_TYPES.includes(this.currentType());
  });
  public isUPN = computed(() => {
    return UPN_TYPES.includes(this.currentType());
  });
  public isAgent = computed(() => {
    return AGENT_TYPES.includes(this.currentType());
  });
  public isAgentInstruction = computed(() => {
    return this.currentType() === MAPS_TYPES_ENUM.AGENT_INSTRUCTION;
  });
  public isAgentInteraction = computed(() => {
    return this.currentType() === MAPS_TYPES_ENUM.AGENT_INTERACTION;
  });

  public readonly tagName = computed(() => {
    return this.config.generalSettings().tagName || this.getFallbackTagName(this.currentType());
  });
  public readonly url = computed(() => {
    return DiagramSubtypeUrlMap[this.currentType()];
  });

  private configCache = new Map<MAPS_TYPES_ENUM, DiagramBaseConfig>();
  private readonly _diagramConfigByType: Record<
    MAPS_TYPES_ENUM,
    {new (diagramMeta: DiagramMetaInterface): DiagramBaseConfig}
  > = {
    [MAPS_TYPES_ENUM.UPN]: UPNConfig,
    [MAPS_TYPES_ENUM.ARCHITECTURE]: ArchitectureBaseConfig,
    [MAPS_TYPES_ENUM.AGENT_INSTRUCTION]: AgentInstructionConfig,
    [MAPS_TYPES_ENUM.AGENT_INTERACTION]: AgentInteractionConfig,
    [MAPS_TYPES_ENUM.INTERACTION_FLOW]: InteractionFlowConfig,
    [MAPS_TYPES_ENUM.CAPABILITY]: CapabilityConfig,
    [MAPS_TYPES_ENUM.DATA_MODEL]: DataModelConfig,
    [MAPS_TYPES_ENUM.CUSTOMER_JOURNEY]: CustomerJourneyConfig,
    [MAPS_TYPES_ENUM.SYSTEM_ARCHITECTURE]: SystemArchitectureConfig,
    [MAPS_TYPES_ENUM.WHITEBOARD]: WhiteboardConfig
  };

  private diagramMeta: DiagramMetaInterface;

  private get config() {
    const type = this.currentType();

    this.setUpConfigType(type);

    return this.configCache.get(type);
  }

  public setDiagramSubtype(subtype: MAPS_TYPES_ENUM) {
    this._currentType.set(subtype);
  }

  public setDiagramConfig(data: DiagramMetaInterface) {
    const subtype = data.subtype || data.category;

    this.diagramMeta = data;
    this._currentType.set(subtype);

    this.setUpConfigType(subtype);
  }

  public generalSettings() {
    return this.config.generalSettings();
  }

  public activitySettings(x?: number, y?: number) {
    return this.config.activitySettings(x, y);
  }

  public flowlineSettings(x?: number, y?: number, id?: number | string) {
    return this.config.flowlineSettings(x, y, id);
  }

  public resourceSettings(resourceCount?: number) {
    return this.config.resourceSettings(resourceCount);
  }

  public get richTextSettings(): RichTextSettings {
    return this.config.richTextSettings;
  }

  public get stickerNoteSettings(): StickerNoteSettings {
    return this.config.stickerNoteSettings;
  }

  public get activityTypes(): ActivityType[] {
    return this.config.activityTypes;
  }

  private setUpConfigType(type: MAPS_TYPES_ENUM): void {
    if (this.configCache.has(type)) {
      return;
    }

    const ConfigClass = this._diagramConfigByType[type];

    if (!ConfigClass) {
      throw new Error(`No configuration found for diagram type: ${type}`);
    }

    const config = new ConfigClass(this.diagramMeta);

    this.configCache.set(type, config);
  }

  private getFallbackTagName(subtype: MAPS_TYPES_ENUM) {
    if (!subtype) {
      return '';
    }

    const words = subtype.replace(/([A-Z])/g, ' $1').trim();

    return words
      .split(' ')
      .map(word => word[0].toUpperCase() + word.slice(1))
      .join(' ');
  }
}
